<template>
  <div class="progress-bar-container rounded progress-bar-gradient">
    <div
      class="progress-bar"
      :class="{ full: percentage >= fullThreshold }"
      :style="progressBarStyle" />
    <div class="labels">
      <span class="start-label">{{ startLabel }}</span>
      <span class="end-label">{{ endLabel }}</span>
    </div>
    <div
      v-for="mark in marks"
      :key="mark.value"
      class="mark"
      :style="`left: ${(mark.value / max) * 100}%`">
      <span class="label"
        ><component
          :is="mark.icon"
          v-if="mark.icon"></component>
        <span v-if="mark.label">{{ mark.label }}</span></span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  interface IProps {
    value: number;
    max?: number;
    fullThreshold?: number;
    useGradient?: boolean;
    marks?: { value: number; label?: string; icon? }[];
    startLabel?: string;
    endLabel?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    max: 100,
    fullThreshold: 100,
    useGradient: false,
    marks: () => [],
    startLabel: '0',
    endLabel: '100',
  });

  const percentage = computed(() => (props.value / props.max) * 100);

  const progressBarStyle = computed(() => {
    const stops =
      props.value > props.fullThreshold
        ? [
            { value: 0, color: 'var(--error)' },
            { value: props.fullThreshold, color: 'var(--warning)' },
            { value: 100, color: 'var(--success)' },
          ]
        : [
            { value: 0, color: 'var(--error)' },
            { value: 100, color: 'var(--warning)' },
          ];
    const gradient = stops.map((stop) => `${stop.color} ${stop.value}%`).join(', ');
    return props.useGradient
      ? {
          background: `linear-gradient(to right, ${gradient})`,
          width: `${percentage.value}%`,
        }
      : {
          width: `${percentage.value}%`,
        };
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  .progress-bar-container {
    height: 1rem;
    width: 100%;
    display: flex;
    position: relative;
    justify-content: flex-start;

    &.progress-bar-gradient {
      background: linear-gradient(to bottom, #eee, #fff);

      box-shadow: 0 0.1rem 0.3rem #00000030;

      .progress-bar {
        border-top: solid 1px #ccc;
        top: -0.1rem;
        position: relative;
        margin-bottom: -0.1rem;
        border-radius: 0.6rem;
        border-bottom-width: 0;

        background: var(--info);

        &.full {
          background: var(--success);
        }
      }
    }

    &.rounded {
      border-radius: 0.6rem;
      border-top: solid 0.1rem #00000033;
      box-shadow: inset 0 0 0.3rem rgba(0, 0, 0, 0.15);
    }

    .mark {
      position: absolute;
      width: 0.1rem;
      background-color: $borders;
      height: calc(100% + 2rem);
      padding: 0;
      margin: -1rem 0;

      .label {
        position: absolute;
        top: -100%;
        transform: translate(-50%, 0.5rem);
        font-size: 0.9em;
        margin-left: 0.1rem;

        svg {
          width: 1.6rem;
        }
      }
    }

    .labels {
      position: absolute;
      top: -2.6rem;
      width: 100%;
      display: flex;
      justify-content: space-between;
      font-weight: 600;
      font-size: 0.9em;
    }
  }
</style>
